import type { ResourceType } from '@medplum/fhirtypes'
import { ResourceAvatar } from '@medplum/react'
import { referenceDisplay, getDetailString } from '@seen/core/display'
import { isProfileResource } from '@seen/core/models/profile'
import { createReference } from '@seen/core/references'
import type { FhirValue } from '@seen/core/utils'
import { cn } from '@seen/ui'
import type { ComponentPropsWithoutRef, JSX, ReactNode } from 'react'
import { Avatar } from '#src/components/core/Avatar/Avatar.tsx'
import { Link } from '#src/components/core/Link.tsx'
import { DevMedplumAppLinkOverlay } from '#src/components/medplum/DevMedplumAppLinkOverlay.tsx'
import { useResource } from '#src/hooks/useResource.ts'

export interface ResourceCardProps<
  T extends ResourceType,
> extends ComponentPropsWithoutRef<'figure'> {
  value: FhirValue<T>
  detail?: JSX.Element
  display?: JSX.Element
  linkTo?: string
  avatar?: ReactNode
  actions?: ReactNode
}

export function ResourceCard<T extends ResourceType>({
  value,
  detail,
  display,
  className,
  avatar,
  linkTo,
  actions,
  ...delegated
}: ResourceCardProps<T>): JSX.Element {
  const { data: resource } = useResource(value)
  const isProfile = isProfileResource(value)
  const displayNode =
    display ?? referenceDisplay(resource && createReference(resource))
  return (
    <figure className={cn('flex items-center gap-3', className)} {...delegated}>
      {isProfile && (avatar ?? <Avatar profile={value} />)}
      {!isProfile && <ResourceAvatar className="size-10" value={value} />}
      {resource && (
        <figcaption className="flex flex-col items-start">
          <p className="flex items-center text-sm font-semibold">
            {linkTo ? <Link to={linkTo}>{displayNode}</Link> : displayNode}
            <DevMedplumAppLinkOverlay to={resource} inline />
          </p>
          {detail}
          {!detail && getDetailString(resource) && (
            <p className="text-sm">{getDetailString(resource)}</p>
          )}
        </figcaption>
      )}
      {actions && <div className="ml-auto">{actions}</div>}
    </figure>
  )
}
